/**
 * 坐标解析工具函数
 * 用于从各种数据格式中提取经纬度坐标
 */

/**
 * 从任意对象中提取经纬度坐标 [lat, lng]
 * 支持多种数据格式：
 * - GeoJSON: { type: 'Point', coordinates: [lng, lat] }
 * - 对象: { lat, lng } 或 { latitude, longitude }
 * - 数组: [lat, lng]
 * - 字符串: "lat,lng" 或 "lat lng"
 * - Google Maps API: { geometry: { location: { lat(), lng() } } }
 */
export function buildLatLng(obj: any): [number, number] | null {
  if (!obj) return null;

  // 处理字符串格式: "lat,lng" 或 "lat lng"
  if (typeof obj === 'string') {
    const parts = obj.split(/[ ,;|]+/).map(s => s.trim()).filter(Boolean);
    if (parts.length === 2) {
      const a = Number(parts[0]);
      const b = Number(parts[1]);
      if (!Number.isNaN(a) && !Number.isNaN(b)) return [a, b];
    }
    return null;
  }

  // 处理数组格式: [lat, lng]
  if (Array.isArray(obj) && obj.length >= 2) {
    const a = Number(obj[0]);
    const b = Number(obj[1]);
    if (!Number.isNaN(a) && !Number.isNaN(b)) return [a, b];
  }

  if (typeof obj !== 'object') return null;

  // GeoJSON 格式: { type: 'Point', coordinates: [lng, lat] }
  if (obj.type && typeof obj.type === 'string' && Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
    const lng = Number(obj.coordinates[0]);
    const lat = Number(obj.coordinates[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return [lat, lng];
  }

  // 标准格式: { latitude, longitude }
  if ((typeof obj.latitude === 'number' || typeof obj.latitude === 'string') && 
      (typeof obj.longitude === 'number' || typeof obj.longitude === 'string')) {
    const la = Number(obj.latitude);
    const lo = Number(obj.longitude);
    if (!Number.isNaN(la) && !Number.isNaN(lo)) return [la, lo];
  }

  // 递归检查 base_payload
  const basePayload = obj.base_payload || obj.basePayload || obj.basePayloadRaw || null;
  if (basePayload && typeof basePayload === 'object') {
    const v = buildLatLng(basePayload);
    if (v) return v;
  }

  // Google Maps API 格式: { geometry: { location: { lat(), lng() } } }
  if (obj.geometry && obj.geometry.location) {
    const loc = obj.geometry.location;
    try {
      const maybeLat = (typeof loc.lat === 'function') ? loc.lat() : (loc.lat ?? loc.latitude ?? null);
      const maybeLng = (typeof loc.lng === 'function') ? loc.lng() : (loc.lng ?? loc.longitude ?? null);
      const na = Number(maybeLat);
      const nb = Number(maybeLng);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return [na, nb];
    } catch (e) {
      // 忽略错误
    }
  }

  // 常见的坐标字段对: lat/lng, latitude/longitude, lat/lon, y/x, phi/lambda
  const pairs: Array<[string, string]> = [
    ['lat', 'lng'],
    ['latitude', 'longitude'],
    ['lat', 'lon'],
    ['y', 'x'],
    ['phi', 'lambda']
  ];
  
  for (const [a, b] of pairs) {
    const va = obj[a];
    const vb = obj[b];
    if ((typeof va === 'number' || typeof va === 'string') && 
        (typeof vb === 'number' || typeof vb === 'string')) {
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return [na, nb];
    }
  }

  // 检查嵌套对象中的常见字段名
  const candidates = [
    'location',
    'geo',
    'geometry',
    'position',
    'coords',
    'coordinate',
    'point',
    'place'
  ];
  
  for (const key of candidates) {
    const sub = obj[key];
    if (!sub) continue;
    const v = buildLatLng(sub);
    if (v) return v;
  }

  // 检查 payload 和 properties
  if (obj.payload && typeof obj.payload === 'object') {
    const v = buildLatLng(obj.payload);
    if (v) return v;
  }

  if (obj.properties && typeof obj.properties === 'object') {
    const v = buildLatLng(obj.properties);
    if (v) return v;
  }

  // 最后尝试：遍历所有属性寻找坐标格式
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    
    // 字符串中的坐标: "lat,lng"
    if (typeof val === 'string' && /[-0-9.+]+\s*[,;|]\s*[-0-9.+]+/.test(val)) {
      const parts = val.split(/[ ,;|]+/).map((s: string) => s.trim());
      if (parts.length >= 2) {
        const a = Number(parts[0]);
        const b = Number(parts[1]);
        if (!Number.isNaN(a) && !Number.isNaN(b)) return [a, b];
      }
    }
    
    // 数组格式的坐标
    if (Array.isArray(val) && val.length >= 2) {
      const a = Number(val[0]);
      const b = Number(val[1]);
      if (!Number.isNaN(a) && !Number.isNaN(b)) return [a, b];
    }
  }

  return null;
}

/**
 * 从 timeline item 中提取坐标信息
 */
export function extractItemCoordinates(item: any): [number, number] | null {
  if (!item) return null;
  
  const src = item?.payload || item?.custom_payload || item?.inline_payload || item;
  return buildLatLng(src) || buildLatLng(item) || null;
}

/**
 * 检查对象是否包含有效坐标
 */
export function hasCoordinates(obj: any): boolean {
  if (!obj) return false;
  
  const src = obj?.payload || obj?.custom_payload || obj?.inline_payload || obj;
  const hasCoord = (src && (
    typeof src.lat === 'number' || 
    typeof src.lng === 'number' || 
    typeof src.latitude === 'number' || 
    typeof src.longitude === 'number'
  )) || (typeof obj?.latitude === 'number' && typeof obj?.longitude === 'number');
  
  return hasCoord;
}
